<template lang="html">
  <div class="brand-autocomplete-field">
    <div class="ui small icon input fluid">
      <input
        type="text"
        placeholder="Brand name"
        v-model="query"
        @input="onQueryInput"
        @focus="showDropdown = true"
        @blur="onBlur"
      />
      <i class="search icon" />
    </div>

    <div
      v-if="showDropdown && (matchingSponsors.length > 0 || query.length > 1)"
      class="ui brand-autocomplete-menu"
    >
      <div
        v-for="sponsor in matchingSponsors"
        :key="sponsor.id"
        class="brand-autocomplete-item"
        @mousedown.prevent="selectSponsor(sponsor)"
      >
        <img
          v-if="sponsorLogo(sponsor)"
          :src="sponsorLogo(sponsor)"
          class="brand-autocomplete-item-logo"
        />
        <span>{{ sponsor.name }}</span>
      </div>

      <div v-if="query.length > 1" class="brand-autocomplete-item brand-autocomplete-domain">
        <span class="brand-autocomplete-domain-label">Not listed? Company domain:</span>
        <div class="ui mini input">
          <input
            type="text"
            v-model="domain"
            placeholder="example.com"
            @mousedown.stop
            @input="onDomainInput"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";

// Mirrors ApplicationHelper#logo_dev_url. The token is a publishable Logo.dev
// client key, already exposed in rendered <img src> attributes elsewhere in
// the app (e.g. onboarding, competitive intelligence) — not a secret.
const LOGO_DEV_TOKEN = "pk_E_Fx0-7gSceC_0F8wOoxLw";

export default {
  data() {
    return {
      query: "",
      domain: "",
      domainTouched: false,
      showDropdown: false,
    };
  },
  computed: {
    ...mapState("report", { report: "report" }),
    ...mapState("sponsor", { sponsors: "all" }),
    matchingSponsors() {
      if (!this.query) return [];
      const q = this.query.toLowerCase();
      return this.sponsors
        .filter((sponsor) => sponsor.name && sponsor.name.toLowerCase().includes(q))
        .slice(0, 6);
    },
  },
  mounted() {
    this.query = (this.report.sections && this.report.sections.brand_name) || "";
    this.$store.dispatch("sponsor/loadAll");
  },
  methods: {
    sponsorLogo(sponsor) {
      return sponsor.logo_url || sponsor.logo_dev_url;
    },
    guessDomain(name) {
      const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "");
      return slug ? `${slug}.com` : "";
    },
    logoDevUrl(domain) {
      return `https://img.logo.dev/${domain}?token=${LOGO_DEV_TOKEN}`;
    },
    onQueryInput() {
      this.showDropdown = true;
      if (!this.domainTouched) {
        this.domain = this.guessDomain(this.query);
      }
      this.applyCustomBrand();
    },
    onDomainInput() {
      this.domainTouched = true;
      this.applyCustomBrand();
    },
    applyCustomBrand() {
      if (!this.query) {
        this.clearBrand();
        return;
      }
      this.$set(this.report.sections, "brand_name", this.query);
      if (this.domain) {
        this.$set(this.report.sections, "brand_logo", {
          type: "logo",
          content: this.logoDevUrl(this.domain),
          name: this.query,
        });
      }
    },
    selectSponsor(sponsor) {
      this.query = sponsor.name;
      this.domain = "";
      this.domainTouched = false;
      this.showDropdown = false;

      this.$set(this.report.sections, "brand_name", sponsor.name);

      const logo = this.sponsorLogo(sponsor);
      if (logo) {
        this.$set(this.report.sections, "brand_logo", {
          type: "logo",
          content: logo,
          name: sponsor.name,
        });
      } else {
        this.$delete(this.report.sections, "brand_logo");
      }
    },
    clearBrand() {
      this.$delete(this.report.sections, "brand_name");
      this.$delete(this.report.sections, "brand_logo");
    },
    onBlur() {
      // Delay so a mousedown on a dropdown item registers before it hides.
      setTimeout(() => {
        this.showDropdown = false;
      }, 150);
    },
  },
};
</script>

<style lang="scss" scoped>
.brand-autocomplete-field {
  position: relative;
  max-width: 260px;
  margin: 0.5rem auto 0;
}

.brand-autocomplete-menu {
  position: absolute;
  z-index: 20;
  top: 100%;
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid rgba(34, 36, 38, 0.15);
  border-radius: 0.28571429rem;
  box-shadow: 0 2px 4px 0 rgba(34, 36, 38, 0.12);
  max-height: 220px;
  overflow-y: auto;
}

.brand-autocomplete-item {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  font-size: 0.9rem;

  &:hover {
    background: rgba(0, 0, 0, 0.03);
  }
}

.brand-autocomplete-item-logo {
  width: 20px;
  height: 20px;
  object-fit: contain;
  margin-right: 0.5rem;
}

.brand-autocomplete-domain {
  flex-direction: column;
  align-items: flex-start;
  border-top: 1px solid rgba(34, 36, 38, 0.1);
  cursor: default;

  &:hover {
    background: none;
  }
}

.brand-autocomplete-domain-label {
  font-size: 0.75rem;
  color: #999;
  margin-bottom: 0.25rem;
}
</style>
